/**
 * DrillView — the closed form vocabulary of the analytics surface.
 *
 * This union is the single source of truth shared by THREE synchronized
 * lists (see A5ai/.claude/skills/form-vocabulary/SKILL.md):
 *   1. this union (frontend state machine + URL params),
 *   2. the ViewRenderer component arms (deterministic rendering, react-query keyed),
 *   3. the bridge ViewRefSchema zod arms (what the LLM may compose).
 * The LLM composes from this vocabulary; it never authors new forms.
 */
import type { FilterCondition } from '../../api/insights';

export type DrillView =
  | { kind: 'inbox' }
  | { kind: 'overview' }
  | { kind: 'customers'; type: 'receivable' | 'payable'; bucketDaysFrom?: number; filters?: FilterCondition[] }
  | { kind: 'customer'; personId: number; personName?: string }
  | { kind: 'invoice'; invoiceId: number }
  | { kind: 'journal'; journalEntryId: number }
  | { kind: 'findings'; scanner?: string }
  | { kind: 'bank' }
  | { kind: 'vat' };

export const todayStr = () => new Date().toISOString().slice(0, 10);

/** Map an aging bucket daysFrom to the person-row field holding its amount. */
export function bucketField(daysFrom: number): string {
  if (daysFrom <= 0) return 'current';
  if (daysFrom <= 30) return 'b1';
  if (daysFrom <= 60) return 'b31';
  if (daysFrom <= 90) return 'b61';
  return 'b90';
}

/** i18n key fragment for a view kind (breadcrumb/label rendering). */
export function viewLabelKey(view: DrillView): string {
  return `analytics.view.${view.kind}`;
}

const VALID_OPS = new Set(['>=', '<=', '==', 'contains']);

function parseFilters(raw: string | undefined): FilterCondition[] | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    const filters = parsed
      .filter(f => f && typeof f === 'object' && typeof f.key === 'string' && VALID_OPS.has(f.op))
      .map(f => ({ key: f.key, op: f.op, value: f.value }))
      .slice(0, 4);
    return filters.length ? filters : undefined;
  } catch {
    return undefined;
  }
}

export function viewToParams(view: DrillView): Record<string, string> {
  switch (view.kind) {
    case 'inbox': return { view: 'inbox' };
    case 'overview': return { view: 'overview' };
    case 'customers': return {
      view: 'customers', type: view.type,
      ...(view.bucketDaysFrom !== undefined ? { bucket: String(view.bucketDaysFrom) } : {}),
      ...(view.filters?.length ? { filters: JSON.stringify(view.filters) } : {}),
    };
    case 'customer': return { view: 'customer', person: String(view.personId), ...(view.personName ? { name: view.personName } : {}) };
    case 'invoice': return { view: 'invoice', invoice: String(view.invoiceId) };
    case 'journal': return { view: 'journal', entry: String(view.journalEntryId) };
    case 'findings': return { view: 'findings', ...(view.scanner ? { scanner: view.scanner } : {}) };
    case 'bank': return { view: 'bank' };
    case 'vat': return { view: 'vat' };
  }
}

export function paramsToView(params: Record<string, string>): DrillView {
  switch (params.view) {
    case 'inbox': return { kind: 'inbox' };
    case 'overview': return { kind: 'overview' };
    case 'customers': return {
      kind: 'customers',
      type: params.type === 'payable' ? 'payable' : 'receivable',
      bucketDaysFrom: params.bucket ? Number(params.bucket) : undefined,
      filters: parseFilters(params.filters),
    };
    case 'customer': return params.person ? { kind: 'customer', personId: Number(params.person), personName: params.name } : { kind: 'overview' };
    case 'invoice': return params.invoice ? { kind: 'invoice', invoiceId: Number(params.invoice) } : { kind: 'overview' };
    case 'journal': return params.entry ? { kind: 'journal', journalEntryId: Number(params.entry) } : { kind: 'overview' };
    case 'findings': return { kind: 'findings', scanner: params.scanner };
    case 'bank': return { kind: 'bank' };
    case 'vat': return { kind: 'vat' };
    default: return { kind: 'inbox' }; // the attention inbox is the landing view
  }
}
